import type { EMPLOYMENT_STATUS, INCOME_FREQUENCY } from './employmentStatus'
import { APPLICATION_DOCUMENTS, LENDER_REQUESTED_DOCUMENTS } from './index'

export const AI_DOCUMENT_READER_FILE_LABELS_DEFAULT_REGION = new Set([
  APPLICATION_DOCUMENTS.MOST_RECENT_PAYSLIP,
  APPLICATION_DOCUMENTS.SECOND_MOST_RECENT_PAYSLIP,
  APPLICATION_DOCUMENTS.FRONT_OF_DRIVERS_LICENCE,
  APPLICATION_DOCUMENTS.BACK_OF_DRIVERS_LICENCE,
  APPLICATION_DOCUMENTS.PASSPORT,
  APPLICATION_DOCUMENTS.FRONT_OF_PHOTO_ID,
  APPLICATION_DOCUMENTS.BACK_OF_PHOTO_ID,
  APPLICATION_DOCUMENTS.TENANCY_AGREEMENT,
  APPLICATION_DOCUMENTS.MEDICARE,
])

export const AI_DOCUMENT_READER_FILE_LABELS_NZ = new Set([
  LENDER_REQUESTED_DOCUMENTS.MOST_RECENT_PAYSLIP,
  LENDER_REQUESTED_DOCUMENTS.SECOND_MOST_RECENT_PAYSLIP,
])

export const AI_DOCUMENT_READER_FILE_LABELS = new Set([
  ...AI_DOCUMENT_READER_FILE_LABELS_DEFAULT_REGION,
  ...AI_DOCUMENT_READER_FILE_LABELS_NZ,
])

export enum AI_DOCUMENT_READER_ERROR_CODE {
  NONE = 0,
  MISMATCHED_DOC_TYPE = 100,
  PASSWORD_PROTECTED = 101,
  CORRUPTED_PDF = 102,
  INVALID_PDF = 103,
  TRAINED_DATA_RETURNED = 104,
  UNSUPPORTED_FILE_FORMAT = 105,
  NO_FILE_CONTENT = 106,
  EMPTY_FILE = 107,
  PDF_RENDER_FAILED = 108,
  TIME_OUT = 200,
  OPENAI_SERVER_ERROR = 300,
  TEXTRACT_SERVER_ERROR = 400,
}

export type AIDocumentReaderFileLabelDefaultRegion =
  | APPLICATION_DOCUMENTS.MOST_RECENT_PAYSLIP
  | APPLICATION_DOCUMENTS.SECOND_MOST_RECENT_PAYSLIP
  | APPLICATION_DOCUMENTS.FRONT_OF_DRIVERS_LICENCE
  | APPLICATION_DOCUMENTS.BACK_OF_DRIVERS_LICENCE
  | APPLICATION_DOCUMENTS.PASSPORT
  | APPLICATION_DOCUMENTS.FRONT_OF_PHOTO_ID
  | APPLICATION_DOCUMENTS.BACK_OF_PHOTO_ID
  | APPLICATION_DOCUMENTS.TENANCY_AGREEMENT
  | APPLICATION_DOCUMENTS.MEDICARE

export type AIDocumentReaderFileLabelNZ =
  | LENDER_REQUESTED_DOCUMENTS.MOST_RECENT_PAYSLIP
  | LENDER_REQUESTED_DOCUMENTS.SECOND_MOST_RECENT_PAYSLIP

export type AIDocumentReaderFileLabel = AIDocumentReaderFileLabelDefaultRegion | AIDocumentReaderFileLabelNZ

export interface AIDocumentReaderResult {
  id: number
  fileId: number
  loanId: number
  fileLabel: AIDocumentReaderFileLabel
  docType: DocumentType | null
  region: DocumentRegion | null
  data: AIDocumentReaderData | null
  confidence: number | null
  isDigitalVersion: 0 | 1 | null
  errorCode: AI_DOCUMENT_READER_ERROR_CODE
}

export enum DocumentType {
  BACK_OF_DRIVERS_LICENCE = 'backOfDriversLicence',
  BACK_OF_PROOF_OF_AGE_CARD = 'backOfProofOfAgeCard',
  FRONT_OF_DRIVERS_LICENCE = 'frontOfDriversLicence',
  FRONT_OF_PROOF_OF_AGE_CARD = 'frontOfProofOfAgeCard',
  MEDICARE = 'medicare',
  OTHER = 'other',
  PASSPORT = 'passport',
  PAYSLIP = 'payslip',
  TENANCY_AGREEMENT = 'tenancyAgreement',
}

export enum DocumentRegion {
  AU_ACT = 'AU-ACT',
  AU_NSW = 'AU-NSW',
  AU_NT = 'AU-NT',
  AU_QLD = 'AU-QLD',
  AU_SA = 'AU-SA',
  AU_TAS = 'AU-TAS',
  AU_VIC = 'AU-VIC',
  AU_WA = 'AU-WA',
  NZ = 'NZ',
  OTHER = 'other',
}

export interface PayslipData {
  employeeName: string | null
  employerName: string | null
  employerAbn: number | null
  employerAcn: string | null
  employmentStatus: EMPLOYMENT_STATUS.FULLTIME | EMPLOYMENT_STATUS.PARTTIME | EMPLOYMENT_STATUS.CASUAL | null
  netIncome: number | null
  grossIncome: number | null
  ytdGrossIncome: number | null
  deductions: number | null
  bonus: number | null
  overtime: number | null
  ordinaryHoursOrUnits: number | null
  isAnnualLeaveAccrued: boolean
  isHolidayPayDetected: boolean | null
  isAcePayslip: boolean | null
  duration: {
    startDate: string | null
    endDate: string | null
  }
  incomeFrequency: INCOME_FREQUENCY | null
  isEmployeeNameMatch: boolean | null
  isEmployerNameMatch: boolean | null
}

export type LicenceClass = 'C' | 'LR' | 'MR' | 'HR' | 'HC' | 'MC' | 'R' | 'RE' | 'R-E' | 'R-N' | 'R-Date' | 'U'
export type LicenceType = 'full' | 'provisional' | 'learner' | 'restricted'
export type LicenceCondition = 'A' | 'B' | 'E' | 'I' | 'N' | 'S' | 'V' | 'X' | 'Z'

export type LicenceClassType = {
  type: LicenceType
  dateOfExpiry: string
}

export interface DriversLicenceData {
  fullName: string | null
  dateOfBirth: string | null
  address: string | null
  addressMatchPercentage: number | null
  licenceNumber: string | null
  cardNumber: string | null
  licenceClasses: Record<LicenceClass, LicenceClassType> | null
  licenceConditions: LicenceCondition[] | null
}

export type PassportType = 'P' | 'D' | 'O' | 'S' | 'L' | 'T'

export type Sex = 'M' | 'F' | 'X'

export interface PassportData {
  fullName: string
  passportNumber: string
  passportType: PassportType | null
  issuingCountry: string
  nationality: string
  sex: Sex
  dateOfBirth: string
  dateOfIssue: string
  dateOfExpiry: string
}

export interface FrontOfProofOfAgeCardData {
  fullName: string
  dateOfBirth: string
  sex: Sex | null
  referenceNumber: string | null
  cardNumber: string | null
  address: string | null
  addressMatchPercentage: number | null
  dateOfIssue: string | null
  dateOfExpiry: string | null
}

export interface BackOfProofOfAgeCardData {
  address: string | null
  addressMatchPercentage: number | null
}

export type PhotoIDData = DriversLicenceData | PassportData | FrontOfProofOfAgeCardData | BackOfProofOfAgeCardData

export type TenancyDocumentKind = 'tenancyAgreement' | 'leaseExtension' | 'variationNotice' | 'other'
export type RentFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'annually' | 'other'

export interface TenancyAgreementData {
  documentKind: TenancyDocumentKind | null
  detectedRegion: DocumentRegion | null
  address: string | null
  tenantNames: string[] | null
  signatoryCount: number | null
  rentAmount: number | null
  rentFrequency: RentFrequency | null
  isRentGrossInclusive: boolean | null
  addressMatchPercentage: number | null
}

export type MedicareCardType = 'green' | 'blue' | 'yellow'

export interface MedicareData {
  cardType: MedicareCardType | null
  cardNumber: string | null
  names: string[] | null
  numberOfIndividuals: number | null
  expiryDate: string | null
}

export type AIDocumentReaderData = PayslipData | PhotoIDData | TenancyAgreementData | MedicareData
